import type { LayerKey } from '@festina-life/api'
import { typeUtils } from '@festina-life/core'
import { Select } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import { useNavigationParams } from '../../system/useNavigationParams'
import {
  collectAllNodesFromTree,
  getItemDepthInTree,
} from '../../uiModels/api/metaModel/helpers'
import { useAllLayersOnServer } from '../../uiModels/api/metaModel/useAllLayersOnServer'
import { useOperationsContext } from '../../workspace/operationsContext'
import { TabbedLocationOption } from './TabbedLocationOption'

type LocationProps = {
  layer: LayerKey
  block: string
}

const validateKey = typeUtils.createValidator<LocationProps>()

type Props = {
  layer: LayerKey
  block: string
  onLayerChange: (layer: LayerKey) => void
  onBlockChange: (block: string) => void
}

export const DraftLocationSelector = ({
  layer,
  block,
  onLayerChange,
  onBlockChange,
}: Props) => {
  const { server } = useNavigationParams()
  const translate = useTranslate()
  const translateLayer = useTranslateLayer()
  const { blockTopology, layerTopology } = useMetaModelContext()
  const { registeredTenantCreateOperations } = useOperationsContext()
  const allBlocks = useMemo(
    () => (blockTopology ? collectAllNodesFromTree(blockTopology) : []),
    [blockTopology],
  )
  const allLayers = useAllLayersOnServer({
    server,
    allTenants: registeredTenantCreateOperations,
  })

  return (
    <div className="flex gap-x-2 min-w-sm w-full">
      <Select
        name={validateKey('block')}
        wrapperProps={{
          label: translate('draftLocationSelector.block.label'),
          className: 'w-full',
        }}
        options={allBlocks}
        shouldShowClearButton={false}
        renderValueFn={(item) => item}
        renderFn={(item) => (
          <TabbedLocationOption
            depth={
              getItemDepthInTree({ itemName: item, tree: blockTopology }) ?? 0
            }
            label={item}
          />
        )}
        value={block}
        onChange={(nextValue) => {
          if (nextValue) {
            onBlockChange(nextValue)
          }
        }}
      />
      <Select
        name={validateKey('layer')}
        wrapperProps={{
          label: translate('draftLocationSelector.layer.label'),
          className: 'w-full',
        }}
        options={allLayers.get(block) ?? []}
        renderValueFn={(item) => translateLayer(item)}
        renderFn={(item) => (
          <TabbedLocationOption
            depth={
              (layerTopology
                ? (getItemDepthInTree({
                    itemName: item.layerId,
                    tree: layerTopology,
                  }) ?? 0)
                : 0) + (item.tenantId ? 1 : 0)
            }
            label={translateLayer(item)}
          />
        )}
        keyFn={(someLayer) => someLayer.toString()}
        shouldShowClearButton={false}
        value={layer}
        onChange={(nextValue) => {
          if (nextValue) {
            onLayerChange(nextValue)
          }
        }}
      />
    </div>
  )
}
